/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { sortBy } from 'lodash'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import Table from 'components/Table'
import { Tooltip } from 'components/Tooltip'
import { PageLoader } from 'components/PageLoader'
import { FIND_ALL_EXTENSION_EXAMS, UPDATE_EXTENSION_EXAM, UPDATE_EXTENSION_EXAM_SECTION } from 'queries/extensionExams'
import {
  findAllExtensionExams,
  findAllExtensionExamsVariables,
  findAllExtensionExams_findAllExtensionExam_items as Exam,
  findAllExtensionExams_findAllExtensionExam_items_extensionExamSection as ExamSection,
} from '__generatedTypes__/findAllExtensionExams'
import { updateExtensionExam, updateExtensionExamVariables } from '__generatedTypes__/updateExtensionExam'
import {
  updateExtensionExamSection,
  updateExtensionExamSectionVariables,
} from '__generatedTypes__/updateExtensionExamSection'
import * as classes from 'pagesNew/QuestionBanks/List/styles'
import { ExamAvailabilityModal } from './ExamAvailabilityModal'
import * as localClasses from './styles'

const EXAM_QUERY_PARAM = 'examId'

export const ExamsList = () => {
  const apolloClient = useApolloClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const examId = searchParams.get(EXAM_QUERY_PARAM)
  const { getLink } = useLink()
  const [selectedExamId, setSelectedExamId] = useState<string | null>(examId ?? null)
  const [isLoading, setIsLoading] = useState(false)
  const [availabilityExam, setAvailabilityExam] = useState<Exam | null>(null)

  const [updateExtensionExam] = useMutation<updateExtensionExam, updateExtensionExamVariables>(UPDATE_EXTENSION_EXAM)
  const [updateExtensionExamSection] = useMutation<updateExtensionExamSection, updateExtensionExamSectionVariables>(
    UPDATE_EXTENSION_EXAM_SECTION
  )
  const { data, loading, refetch } = useQuery<findAllExtensionExams, findAllExtensionExamsVariables>(
    FIND_ALL_EXTENSION_EXAMS,
    {
      variables: {
        query: {},
      },
      fetchPolicy: 'network-only',
    }
  )
  const exams = data?.findAllExtensionExam.items ?? []

  useEffect(() => {
    if (!exams.length) {
      return
    }

    setSelectedExamId((prevSelectedExamId) =>
      exams.find(({ id }) => id === prevSelectedExamId) ? prevSelectedExamId : exams[0]?.id ?? null
    )
  }, [exams])

  const selectedExam = useMemo(() => {
    const exam = exams.find(({ id }) => id === selectedExamId)

    if (!exam) {
      return null
    }

    return {
      ...exam,
      extensionExamSection: sortBy(exam.extensionExamSection ?? [], 'order'),
    }
  }, [exams, selectedExamId])

  const handleShowSolutionExamChange = useCallback(
    async (exam: Exam, showSolutionUpd: boolean) => {
      setIsLoading(true)

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'ExtensionExamType',
          id: exam.id,
        }),
        fields: {
          showSolution: () => showSolutionUpd,
        },
      })

      try {
        await updateExtensionExam({
          variables: {
            params: {
              id: exam.id,
              showSolution: showSolutionUpd,
            },
          },
        })
        toast.success('Exam updated successfully')
      } catch (e) {
        toast.error('Exam update failed')
        await refetch()
      }
      setIsLoading(false)
    },
    [refetch]
  )

  const handleUpdateExamAvailability = useCallback(
    async (examUpd: Exam) => {
      setIsLoading(true)

      // Solutions display makes no sense once the whole extension interface is hidden
      const showSolutionUpd = examUpd.showExam ? examUpd.showSolution : false

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'ExtensionExamType',
          id: examUpd.id,
        }),
        fields: {
          showExam: () => examUpd.showExam,
          message: () => examUpd.message,
          showSolution: () => showSolutionUpd,
        },
      })

      try {
        await updateExtensionExam({
          variables: {
            params: {
              id: examUpd.id,
              showExam: examUpd.showExam,
              message: examUpd.message,
              showSolution: showSolutionUpd,
            },
          },
        })
        toast.success('Exam updated successfully')
        setAvailabilityExam(null)
      } catch (e) {
        toast.error('Exam update failed')
        await refetch()
      }
      setIsLoading(false)
    },
    [refetch]
  )

  const handleShowSolutionSectionChange = useCallback(
    async (section: ExamSection | undefined, exam: Exam | null, showSolutionUpd: boolean) => {
      if (!section || !exam) {
        return
      }

      setIsLoading(true)

      const isShowSolutionSectionExists = (exam.extensionExamSection ?? []).some((examSection) =>
        examSection.id === section.id ? showSolutionUpd : examSection.showSolution
      )

      try {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({
            __typename: 'ExtensionExamSectionType',
            id: section.id,
          }),
          fields: {
            showSolution: () => showSolutionUpd,
          },
        })

        if (!isShowSolutionSectionExists) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({
              __typename: 'ExtensionExamType',
              id: exam.id,
            }),
            fields: {
              showSolution: () => false,
            },
          })
        }

        await updateExtensionExamSection({
          variables: {
            params: {
              id: section.id,
              showSolution: showSolutionUpd,
            },
          },
        })

        if (!isShowSolutionSectionExists) {
          await updateExtensionExam({
            variables: {
              params: {
                id: exam.id,
                showSolution: false,
              },
            },
          })
        }
        toast.success('Exam Section updated successfully')
      } catch (e) {
        toast.error('Exam Section update failed')
        await refetch()
      }
      setIsLoading(false)
    },
    [refetch]
  )

  const handleSelectExam = useCallback((exam: Exam) => {
    setSearchParams((prev) => ({
      ...Object.fromEntries(prev.entries()),
      [EXAM_QUERY_PARAM]: exam.id,
    }))
    setSelectedExamId(exam.id)
  }, [])

  if (loading) {
    return <PageLoader />
  }

  return (
    <div css={classes.tablesWrapper}>
      <div css={classes.categoriesWrapper}>
        <Typography variant="body1" css={[classes.tableHeader, classes.tableTitle]}>
          Official AAMC Materials
        </Typography>
        <Table<Exam>
          css={classes.categoriesTable}
          columns={[
            {
              title: 'Title',
              renderCell: (exam) => {
                const isExamSolutionOff = exam.showExam && !exam.showSolution
                const hasSectionMissingSolutions =
                  exam.showExam &&
                  exam.showSolution &&
                  exam.extensionExamSection?.some((section) => !section.showSolution)

                return (
                  <div css={localClasses.titleCell}>
                    {exam.title}
                    {!exam.showExam && <Chip label="Extension off" size="small" css={localClasses.disabledBadge} />}
                    {isExamSolutionOff && (
                      <Chip label="Hidden solutions" size="small" css={localClasses.disabledBadge} />
                    )}
                    {hasSectionMissingSolutions && (
                      <Chip label="Missing solutions" size="small" css={localClasses.warningBadge} />
                    )}
                  </div>
                )
              },
            },
            {
              title: 'Live',
              renderCell: (exam) => {
                if (!exam.showExam) {
                  return (
                    <Tooltip
                      title="You cannot enable Live (show solutions) for Exam when Extension Interface is turned off"
                      isNote
                      arrow
                    >
                      <Switch checked={exam.showSolution} onClick={(e) => e.stopPropagation()} disabled />
                    </Tooltip>
                  )
                }

                const isShowSolutionSectionExists = exam.extensionExamSection?.some(({ showSolution }) => showSolution)

                if (isShowSolutionSectionExists) {
                  return (
                    <Switch
                      checked={exam.showSolution}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(_e, showSolution) => handleShowSolutionExamChange(exam, showSolution)}
                      disabled={isLoading}
                    />
                  )
                }

                return (
                  <Tooltip
                    title="You cannot enable Live (show solutions) for Exam with an empty list of Exam Sections with Live (show solutions) enabled"
                    isNote
                    arrow
                  >
                    <Switch
                      checked={exam.showSolution}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(_e, showSolution) => handleShowSolutionExamChange(exam, showSolution)}
                      disabled
                    />
                  </Tooltip>
                )
              },
              flex: '0 1 50px',
            },
            {
              renderCell: (exam) => (
                <IconButton
                  size="small"
                  css={classes.link}
                  onClick={(e) => {
                    e.stopPropagation()
                    setAvailabilityExam(exam)
                  }}
                >
                  <EditIcon />
                </IconButton>
              ),
              flex: '0 1 30px',
            },
          ]}
          items={exams}
          activeItem={selectedExam ?? undefined}
          onItemClick={handleSelectExam}
        />
      </div>
      <div css={classes.questionBanksWrapper}>
        <Typography variant="body1" css={[classes.tableHeader, classes.tableTitle]}>
          Individual Sections
        </Typography>
        <Table<ExamSection>
          css={classes.questionBanksTable}
          columns={[
            {
              title: 'Title',
              renderCell: (section) => section.title,
            },
            {
              title: (
                <div css={classes.countWrapper}>
                  <div css={classes.countTitle}># of Solutions</div>
                </div>
              ),
              renderCell: (section) => (
                <div css={classes.countValue}>
                  {section.solutionCount}/{section.questionCount}
                </div>
              ),
              flex: '0 1 50px',
            },
            {
              title: 'Live',
              renderCell: (section) => {
                if (!section.solutionCount || section.solutionCount !== section.questionCount) {
                  return (
                    <Tooltip
                      title="You cannot enable Live (show solutions) for Exam Section if any questions are missing solutions"
                      isNote
                      arrow
                    >
                      <Switch
                        checked={section.showSolution}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(_e, showSolution) =>
                          handleShowSolutionSectionChange(section, selectedExam, showSolution)
                        }
                        disabled
                      />
                    </Tooltip>
                  )
                }

                return (
                  <Switch
                    checked={section.showSolution}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_e, showSolution) =>
                      handleShowSolutionSectionChange(section, selectedExam, showSolution)
                    }
                    disabled={isLoading}
                  />
                )
              },
              flex: '0 1 50px',
            },
            {
              renderCell: (section) => (
                <Link to={getLink(cmsRoutes.CARS_PLUGIN_EXAM_SECTION, { sectionId: section.id })}>
                  <IconButton size="small" css={classes.link}>
                    <EditIcon />
                  </IconButton>
                </Link>
              ),
              flex: '0 1 30px',
            },
          ]}
          items={selectedExam?.extensionExamSection ?? []}
        />
      </div>
      <ExamAvailabilityModal
        isOpen={!!availabilityExam}
        exam={availabilityExam}
        onSubmit={handleUpdateExamAvailability}
        onClose={() => setAvailabilityExam(null)}
      />
    </div>
  )
}
